import { useState, useEffect } from 'react';
import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import { Navigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Search, RefreshCw, Shield, CheckCircle, AlertTriangle,
  XCircle, Globe, FileText, Clock, TrendingUp
} from 'lucide-react';

interface PageAudit {
  url: string;
  status: number | null;
  title: string | null;
  description: string | null;
  h1: string | null;
  canonical: string | null;
  hasOgImage: boolean;
  loadTimeMs: number | null;
  issues: string[];
}

interface SitemapUrl {
  loc: string;
  lastmod?: string;
  changefreq?: string;
  priority?: string;
}

interface AuditSummary {
  totalPages: number;
  healthyPages: number;
  totalIssues: number;
  avgLoadTimeMs: number;
  score: number;
}

interface AuditResult {
  siteUrl: string;
  sitemapUrls: SitemapUrl[];
  audits: PageAudit[];
  robotsOk: boolean;
  summary: AuditSummary;
}

export default function AdminSEO() {
  const { user } = useAuth();
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuditing, setIsAuditing] = useState(false);
  const [result, setResult] = useState<AuditResult | null>(null);
  const [lastAuditTime, setLastAuditTime] = useState<Date | null>(null);

  useEffect(() => {
    checkAdmin();
  }, [user]);

  const checkAdmin = async () => {
    if (!user) return;
    const { data } = await supabase
      .from('user_roles')
      .select('role')
      .eq('user_id', user.id)
      .eq('role', 'admin')
      .maybeSingle();
    setIsAdmin(!!data);
    setIsLoading(false);
  };

  const runAudit = async () => {
    setIsAuditing(true);
    try {
      const { data: { session } } = await (supabase.auth as any).getSession();
      const res = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/seo-audit`,
        {
          headers: {
            Authorization: `Bearer ${session?.access_token}`,
            'Content-Type': 'application/json',
          },
        }
      );
      if (!res.ok) throw new Error('Audit failed');
      const data = await res.json();
      setResult(data);
      setLastAuditTime(new Date());
      toast.success('SEO audit complete');
    } catch (err) {
      toast.error('Failed to run SEO audit');
    } finally {
      setIsAuditing(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  if (isAdmin === false) return <Navigate to="/dashboard" replace />;

  const getScoreColor = (score: number) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 50) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getStatusBadge = (issues: string[]) => {
    if (issues.length === 0) return <Badge className="bg-green-100 text-green-800 border-green-200"><CheckCircle className="h-3 w-3 mr-1" />Healthy</Badge>;
    if (issues.length <= 2) return <Badge className="bg-yellow-100 text-yellow-800 border-yellow-200"><AlertTriangle className="h-3 w-3 mr-1" />{issues.length} issues</Badge>;
    return <Badge className="bg-red-100 text-red-800 border-red-200"><XCircle className="h-3 w-3 mr-1" />{issues.length} issues</Badge>;
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="container py-8">
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-foreground flex items-center gap-2">
              <Search className="h-8 w-8 text-primary" />
              SEO Dashboard
            </h1>
            <p className="text-muted-foreground mt-1">
              Monitor search health & page optimization
              {lastAuditTime && (
                <span className="ml-2 text-xs">
                  · Last audit: {lastAuditTime.toLocaleTimeString()}
                </span>
              )}
            </p>
          </div>
          <Button onClick={runAudit} disabled={isAuditing}>
            <RefreshCw className={`h-4 w-4 mr-2 ${isAuditing ? 'animate-spin' : ''}`} />
            {isAuditing ? 'Auditing...' : 'Run Audit'}
          </Button>
        </div>

        {!result && !isAuditing && (
          <Card className="border-dashed">
            <CardContent className="flex flex-col items-center justify-center py-16">
              <Search className="h-16 w-16 text-muted-foreground/30 mb-4" />
              <h3 className="text-lg font-medium text-foreground mb-2">No audit data yet</h3>
              <p className="text-muted-foreground mb-6">Run an SEO audit to check your site's health</p>
              <Button onClick={runAudit}>
                <TrendingUp className="h-4 w-4 mr-2" />
                Start First Audit
              </Button>
            </CardContent>
          </Card>
        )}

        {isAuditing && (
          <div className="grid gap-4 md:grid-cols-4">
            {[...Array(4)].map((_, i) => (
              <Card key={i}><CardContent className="p-6"><Skeleton className="h-20" /></CardContent></Card>
            ))}
          </div>
        )}

        {result && !isAuditing && (
          <>
            {/* Summary Cards */}
            <div className="grid gap-4 md:grid-cols-4 mb-8">
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center gap-3">
                    <div className="rounded-full bg-primary/10 p-3">
                      <TrendingUp className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">SEO Score</p>
                      <p className={`text-3xl font-bold ${getScoreColor(result.summary.score)}`}>
                        {result.summary.score}%
                      </p>
                    </div>
                  </div>
                  <Progress value={result.summary.score} className="mt-3" />
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center gap-3">
                    <div className="rounded-full bg-green-100 p-3">
                      <Globe className="h-5 w-5 text-green-600" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Pages Indexed</p>
                      <p className="text-3xl font-bold text-foreground">{result.summary.totalPages}</p>
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">
                    {result.summary.healthyPages} healthy · {result.summary.totalPages - result.summary.healthyPages} with issues
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center gap-3">
                    <div className="rounded-full bg-yellow-100 p-3">
                      <AlertTriangle className="h-5 w-5 text-yellow-600" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Total Issues</p>
                      <p className="text-3xl font-bold text-foreground">{result.summary.totalIssues}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center gap-3">
                    <div className="rounded-full bg-blue-100 p-3">
                      <Clock className="h-5 w-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Avg Load Time</p>
                      <p className="text-3xl font-bold text-foreground">{result.summary.avgLoadTimeMs}ms</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Infrastructure Checks */}
            <Card className="mb-8">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="h-5 w-5" />
                  Infrastructure
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid gap-3 md:grid-cols-3">
                  <div className="flex items-center gap-2">
                    {result.robotsOk ? (
                      <CheckCircle className="h-4 w-4 text-green-600" />
                    ) : (
                      <XCircle className="h-4 w-4 text-red-600" />
                    )}
                    <span className="text-sm">robots.txt</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {result.sitemapUrls.length > 0 ? (
                      <CheckCircle className="h-4 w-4 text-green-600" />
                    ) : (
                      <XCircle className="h-4 w-4 text-red-600" />
                    )}
                    <span className="text-sm">sitemap.xml ({result.sitemapUrls.length} URLs)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    <span className="text-sm">HTTPS enabled</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Tabs defaultValue="pages">
              <TabsList>
                <TabsTrigger value="pages">
                  <FileText className="h-4 w-4 mr-1.5" />
                  Page Audit
                </TabsTrigger>
                <TabsTrigger value="sitemap">
                  <Globe className="h-4 w-4 mr-1.5" />
                  Sitemap
                </TabsTrigger>
              </TabsList>

              <TabsContent value="pages">
                <Card>
                  <CardHeader>
                    <CardTitle>Page-by-Page Audit</CardTitle>
                    <CardDescription>SEO health check for each page in your sitemap</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>URL</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Title</TableHead>
                          <TableHead>Load Time</TableHead>
                          <TableHead>Health</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {result.audits.map((audit) => (
                          <TableRow key={audit.url}>
                            <TableCell className="font-mono text-xs max-w-[200px] truncate">
                              {new URL(audit.url).pathname || '/'}
                            </TableCell>
                            <TableCell>
                              <Badge variant={audit.status === 200 ? 'default' : 'destructive'}>
                                {audit.status || 'ERR'}
                              </Badge>
                            </TableCell>
                            <TableCell className="max-w-[200px] truncate text-sm">
                              {audit.title || '—'}
                            </TableCell>
                            <TableCell className="text-sm">
                              {audit.loadTimeMs ? `${audit.loadTimeMs}ms` : '—'}
                            </TableCell>
                            <TableCell>{getStatusBadge(audit.issues)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>

                    {/* Issue Details */}
                    {result.audits.some(a => a.issues.length > 0) && (
                      <div className="mt-6 space-y-3">
                        <h4 className="font-medium text-foreground">Issues Found</h4>
                        {result.audits
                          .filter(a => a.issues.length > 0)
                          .map((audit) => (
                            <div key={audit.url} className="rounded-lg border p-3">
                              <p className="font-mono text-xs text-muted-foreground mb-1">
                                {new URL(audit.url).pathname || '/'}
                              </p>
                              <ul className="space-y-1">
                                {audit.issues.map((issue, i) => (
                                  <li key={i} className="text-sm flex items-center gap-2">
                                    <AlertTriangle className="h-3 w-3 text-yellow-600 flex-shrink-0" />
                                    {issue}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="sitemap">
                <Card>
                  <CardHeader>
                    <CardTitle>Sitemap URLs</CardTitle>
                    <CardDescription>All URLs listed in your sitemap.xml</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>URL</TableHead>
                          <TableHead>Last Modified</TableHead>
                          <TableHead>Frequency</TableHead>
                          <TableHead>Priority</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {result.sitemapUrls.map((sUrl) => (
                          <TableRow key={sUrl.loc}>
                            <TableCell className="font-mono text-xs">
                              {new URL(sUrl.loc).pathname || '/'}
                            </TableCell>
                            <TableCell className="text-sm">{sUrl.lastmod || '—'}</TableCell>
                            <TableCell className="text-sm">{sUrl.changefreq || '—'}</TableCell>
                            <TableCell className="text-sm">{sUrl.priority || '—'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </>
        )}
      </main>
      <Footer />
    </div>
  );
}
